import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { UserPlus } from "lucide-react";
import { type FC, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as accountApi from "../api/account";
import * as characterApi from "../api/character";
import * as chatRoomApi from "../api/chat-room";
import * as llmApi from "../api/llm";
import { CharacterItem } from "../components/character-item";
import { CreateCharacterModal } from "../components/create-character-modal";
import { Header } from "../components/header";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import type * as types from "../lib/types";

export const Character: FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [characters, setCharacters] = useState<types.Character[]>([]);
  const [allCharacters, setAllCharacters] = useState<types.Character[]>([]);
  const [models, setModels] = useState<llmApi.LLMModel[]>([]);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { user } = useAuthenticator((context) => [context.user]);

  useEffect(() => {
    const init = async () => {
      try {
        const authSession = await fetchAuthSession();
        const maybeToken = authSession.tokens?.idToken?.toString();
        if (!maybeToken) {
          toast.error("認証情報が見つかりません。リロードして再度ログインしてください。");
          return;
        }
        setToken(maybeToken);

        const maybeAccountId = user?.userId;
        const maybeEmail = user?.signInDetails?.loginId;
        console.log("User ID:", maybeAccountId, "Email:", maybeEmail);
        if (!maybeAccountId || !maybeEmail) {
          toast.error("アカウント情報が見つかりません。リロードして再度ログインしてください。");
          return;
        }

        // Check if account exists, create if not
        try {
          await accountApi.getAccount(maybeToken, maybeAccountId);
        } catch (error) {
          // Account doesn't exist, create it
          try {
            await accountApi.createAccount(maybeToken, {
              id: maybeAccountId,
              email: maybeEmail,
            });
          } catch (createError) {
            console.error("Failed to create account:", createError);
            toast.error("アカウントの作成に失敗しました");
            return;
          }
        }

        setAccountId(maybeAccountId);

        const { llm_models } = await llmApi.listLlmModels(maybeToken);
        if (!llm_models || llm_models.length === 0) {
          toast.error("LLMモデルが見つかりません");
          return;
        }
        setModels(llm_models);

        const { characters } = await characterApi.listCharacters(maybeToken);
        setCharacters(characters);

        const { characters: allCharacters } = await characterApi.listCharacters(maybeToken);
        setAllCharacters(allCharacters);

        setError(null);
        toast.success("キャラクター一覧を取得しました");
      } catch (error) {
        console.error(error);
        setError("キャラクター一覧の取得に失敗しました");
        toast.error("キャラクター一覧の取得に失敗しました");
      }
    };

    init();
  }, [user?.userId]);

  const handleCreateCharacter = useCallback(
    async (name: string, uniqueName: string, model: llmApi.LLMModel, initialRoomName: string) => {
      if (!token || !accountId) {
        toast.error("認証情報が見つかりません\nリロードして再度ログインしてください");
        return;
      }

      try {
        const { chat_room_id, character_id } = await chatRoomApi.createChatRoomAndCharacterRoom(token, {
          name: initialRoomName,
          character_name: name,
          character_unique_name: uniqueName,
          llm_model_id: model.id,
        });

        await chatRoomApi.joinChatRoom(token, chat_room_id, accountId);

        setIsCreateModalOpen(false);
        navigate(`/characters/${character_id}/chat-rooms`);
        toast.success("キャラクターとデータ管理タグを作成しました");
      } catch (error) {
        console.error(error);
        toast.error("キャラクターとデータ管理タグの作成に失敗しました");
      }
    },
    [token, accountId, navigate],
  );

  return (
    <div className="h-screen flex flex-col bg-white">
      <Header />
      <div className="p-4 border-b">
        <h1 className="text-2xl font-bold mb-4">キャラクター一覧</h1>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {error ? (
            <div className="text-center p-4 text-red-500">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal mb-2"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                キャラクターを作成
              </Button>
              {characters.map((character) => (
                <CharacterItem
                  key={character.id}
                  character={character}
                  onClick={() => navigate(`/characters/${character.id}/chat-rooms`)}
                />
              ))}
            </>
          )}
        </div>
      </ScrollArea>
      <CreateCharacterModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateCharacter={handleCreateCharacter}
        models={models}
        characters={allCharacters}
      />
    </div>
  );
};
